import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightIcon, CheckCircleIcon, ChatBubbleLeftRightIcon,
  ChevronLeftIcon, ChevronRightIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import logo from '../../assets/images/logo.jpeg';

export const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');`;
export const WA_NUMBER = '254748798287';

export const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

// ─── HERO CAROUSEL ────────────────────────────────────────────────────────────
export const HeroCarousel = ({ photos = [], tagline, heroText, waMessage }) => {
  const imgs = photos.length > 0 ? photos : [logo, logo, logo];
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const INTERVAL = 5000;

  // RAF-driven progress + auto-advance (matches Slider.js pattern)
  useEffect(() => {
    setProgress(0);
    startRef.current = null;
    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const pct = Math.min(((ts - startRef.current) / INTERVAL) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setIdx(i => (i + 1) % imgs.length);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [idx, imgs.length]);

  const goTo = (i) => { cancelAnimationFrame(rafRef.current); setIdx(i); };
  const prev = () => goTo((idx - 1 + imgs.length) % imgs.length);
  const next = () => goTo((idx + 1) % imgs.length);

  const textVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { delay: 0.15 + i * 0.12, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: 'clamp(360px,50vw,450px)' }}>

      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.75, ease: [0.77,0,0.18,1] }}
          className="absolute inset-0">
          <img src={imgs[idx]} alt="" className="w-full h-full object-cover" />
        </motion.div>
      </AnimatePresence>

      {/* Overlays — left-heavy dark, right lets photo breathe */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.12) 75%, transparent 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 40%)' }} />

      {/* Left red accent line (matches Slider.js) */}
      <motion.div
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.7, ease: [0.22,1,0.36,1] }}
        className="absolute left-0 top-0 bottom-0 w-1 origin-top hidden md:block"
        style={{ background: 'linear-gradient(to bottom, #800020, rgba(128,0,32,0.3), transparent)' }}
      />

      {/* Text — left-aligned, matching Slider layout */}
      <div className="absolute inset-0 flex items-center" style={{ zIndex: 2 }}>
        <div className="container-custom w-full px-6 md:px-8 lg:px-12">
          <div className="max-w-xl pl-4 md:pl-6">

            <motion.span
              custom={0} variants={textVariants} initial="hidden" animate="visible"
              className="inline-block text-xs font-bold tracking-[0.22em] uppercase mb-4 text-white/60">
              PowerCore Services
            </motion.span>

            <motion.h1
              custom={1} variants={textVariants} initial="hidden" animate="visible"
              className="font-black text-white leading-none mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.8rem,6.5vw,5rem)', letterSpacing: '-0.02em', textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>
              {tagline}
            </motion.h1>

            <motion.p
              custom={2} variants={textVariants} initial="hidden" animate="visible"
              className="mb-8 leading-relaxed font-light text-gray-300"
              style={{ fontSize: 'clamp(0.85rem,1.4vw,1rem)', maxWidth: '420px', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
              {heroText}
            </motion.p>

            <motion.div
              custom={3} variants={textVariants} initial="hidden" animate="visible"
              className="flex flex-wrap gap-3">
              <a href={`https://wa.me/${WA_NUMBER}?text=${waMessage}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 font-bold text-sm text-white transition-all duration-300 rounded-sm hover:-translate-y-0.5"
                style={{ background: '#800020', letterSpacing: '0.05em', boxShadow: '0 4px 24px rgba(128,0,32,0.45)' }}
                onMouseEnter={e => { e.currentTarget.style.background='#9a0025'; e.currentTarget.style.boxShadow='0 6px 32px rgba(128,0,32,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#800020'; e.currentTarget.style.boxShadow='0 4px 24px rgba(128,0,32,0.45)'; }}>
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                Enquire on WhatsApp
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
              <Link to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 font-bold text-sm text-white transition-all duration-300 rounded-sm hover:-translate-y-0.5"
                style={{ background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', letterSpacing: '0.05em' }}>
                View Plans
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slide counter bottom-left (matches Slider.js) */}
      <div className="absolute bottom-14 left-8 md:left-12 flex items-baseline gap-1" style={{ zIndex: 3 }}>
        <span className="text-white font-black text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {String(idx + 1).padStart(2, '0')}
        </span>
        <span className="text-white/40 text-xs font-light">/ {String(imgs.length).padStart(2, '0')}</span>
      </div>

      {/* Nav arrows */}
      {imgs.length > 1 && (<>
        <button onClick={prev} aria-label="Previous"
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full transition-all duration-300"
          style={{ width: 'clamp(36px,4vw,44px)', height: 'clamp(36px,4vw,44px)', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}
          onMouseEnter={e => { e.currentTarget.style.background='rgba(128,0,32,0.75)'; e.currentTarget.style.borderColor='rgba(128,0,32,0.8)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.12)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'; }}>
          <ChevronLeftIcon className="text-white" style={{ width: 'clamp(14px,2vw,18px)', height: 'clamp(14px,2vw,18px)' }} />
        </button>
        <button onClick={next} aria-label="Next"
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full transition-all duration-300"
          style={{ width: 'clamp(36px,4vw,44px)', height: 'clamp(36px,4vw,44px)', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}
          onMouseEnter={e => { e.currentTarget.style.background='rgba(128,0,32,0.75)'; e.currentTarget.style.borderColor='rgba(128,0,32,0.8)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.12)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.2)'; }}>
          <ChevronRightIcon className="text-white" style={{ width: 'clamp(14px,2vw,18px)', height: 'clamp(14px,2vw,18px)' }} />
        </button>
      </>)}

      {/* Dot indicators (matches Slider.js animated dots) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
        {imgs.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} aria-label={`Slide ${i+1}`}>
            <motion.div
              animate={{ width: i === idx ? 40 : 12, backgroundColor: i === idx ? '#800020' : 'rgba(255,255,255,0.45)' }}
              transition={{ duration: 0.3, ease: [0.22,1,0.36,1] }}
              className="h-2 rounded-full"
            />
          </button>
        ))}
      </div>

      {/* RAF progress bar bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10" style={{ height: '2px', background: 'rgba(255,255,255,0.1)' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#800020,#c0002f)', transition: 'width 0.05s linear' }} />
      </div>

      {/* White fade into page */}
      <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to top,white,transparent)' }} />
    </section>
  );
};

// ─── FEATURES GRID ────────────────────────────────────────────────────────────
export const FeaturesGrid = ({ features, sectionTitle, sectionLabel }) => (
  <section className="py-16 bg-white">
    <div className="container-custom">
      <motion.div {...fade(0)} className="text-center mb-12">
        <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>
          {sectionLabel}
        </span>
        <h2 className="font-black text-gray-900"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
          {sectionTitle}
        </h2>
      </motion.div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((f, i) => (
          <motion.div key={i} {...fade(i * 0.07)}
            className="rounded-3xl p-7 border border-gray-100 hover:border-red-100 hover:shadow-lg transition-all duration-300 group"
            style={{ background: '#faf8f8' }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110"
              style={{ background: '#fef2f2', color: '#800020' }}>
              {f.icon}
            </div>
            <h3 className="font-black text-gray-900 mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem' }}>
              {f.title.toUpperCase()}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─── BENEFITS + CTA ───────────────────────────────────────────────────────────
export const BenefitsCTA = ({ benefits, benefitsTitle, ctaTitle, ctaText, waMessage }) => (
  <section className="py-16" style={{ background: '#faf8f8' }}>
    <div className="container-custom">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <motion.div {...fade(0)}>
          <span className="text-xs font-bold tracking-[0.2em] uppercase mb-3 inline-block" style={{ color: '#800020' }}>
            Benefits
          </span>
          <h2 className="font-black text-gray-900 mb-6 leading-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
            {benefitsTitle}
          </h2>
          <div className="space-y-3">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#800020' }} />
                <span className="text-sm text-gray-600">{b}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fade(0.15)} className="space-y-4">
          <div className="rounded-3xl p-8 text-white"
            style={{ background: 'linear-gradient(135deg,#800020,#3d0010)' }}>
            <h3 className="font-black text-2xl mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {ctaTitle}
            </h3>
            <p className="text-white/75 text-sm mb-6">{ctaText}</p>
            <a href={`https://wa.me/${WA_NUMBER}?text=${waMessage}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-white transition-all hover:scale-105"
              style={{ color: '#800020' }}>
              <ChatBubbleLeftRightIcon className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
          <div className="rounded-3xl p-6 border-2 border-dashed border-red-100 text-center">
            <p className="text-sm text-gray-500 mb-3">Explore all activities available at PowerCore</p>
            <Link to="/activities"
              className="inline-flex items-center gap-2 text-sm font-bold transition-all hover:gap-3"
              style={{ color: '#800020' }}>
              View All Activities <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// ─── ACTIVITIES CTA ───────────────────────────────────────────────────────────
export const ActivitiesCTA = () => (
  <section className="py-12 bg-white">
    <div className="container-custom">
      <motion.div {...fade(0)}
        className="rounded-3xl overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 px-8 py-8"
        style={{ background: 'linear-gradient(135deg,#fef2f2 0%,#fdf5f7 100%)', border: '1px solid #fcd5de' }}>
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#800020' }}>
            See What's On
          </p>
          <h3 className="font-black text-gray-900 text-2xl mb-1"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            EXPLORE ALL ACTIVITIES AT POWERCORE
          </h3>
          <p className="text-sm text-gray-500">
            Browse our full activity schedule, class times and enquire directly for any session.
          </p>
        </div>
        <Link to="/activities"
          className="flex-shrink-0 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm text-white transition-all hover:scale-105 hover:shadow-lg whitespace-nowrap"
          style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.25)' }}>
          View All Activities <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

// ─── PHOTO GALLERY ────────────────────────────────────────────────────────────
// Lightbox-enabled gallery with unorganised masonry-style grid
// photos: array of image URLs. Falls back to logo repeated.
export const PhotoGallery = ({ photos = [], title = 'INSIDE THE EXPERIENCE' }) => {
  const imgs = photos.length >= 6
    ? photos
    : Array(9).fill(logo); // repeat logo as placeholder

  const [lightbox, setLightbox] = useState(null); // index or null

  const prev = () => setLightbox(i => (i - 1 + imgs.length) % imgs.length);
  const next = () => setLightbox(i => (i + 1) % imgs.length);

  // Keyboard nav
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  // Build a varied layout: mix of sizes using CSS grid spans
  const spans = [
    'col-span-2 row-span-2', // big
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-1 row-span-2', // tall
    'col-span-1 row-span-1',
    'col-span-2 row-span-1', // wide
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
  ];

  return (
    <section className="py-16" style={{ background: '#0d0d0d' }}>
      <div className="container-custom">
        <motion.div {...fade(0)} className="text-center mb-10">
          <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#e8a0b0' }}>
            Gallery
          </span>
          <h2 className="font-black text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
            {title}
          </h2>
        </motion.div>

        {/* Unorganised asymmetric grid */}
        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridAutoRows: '160px',
          }}>
          {imgs.slice(0, 9).map((src, i) => (
            <motion.div
              key={i}
              {...fade(i * 0.04)}
              className={`${spans[i] || 'col-span-1 row-span-1'} overflow-hidden rounded-2xl cursor-pointer group relative`}
              onClick={() => setLightbox(i)}>
              <img
                src={src}
                alt={`Gallery ${i + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold tracking-widest uppercase">
                  View
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Show more row if many photos */}
        {imgs.length > 9 && (
          <div
            className="grid gap-2 mt-2"
            style={{ gridTemplateColumns: 'repeat(4, 1fr)', gridAutoRows: '120px' }}>
            {imgs.slice(9, 13).map((src, i) => (
              <motion.div
                key={i + 9}
                {...fade(i * 0.04)}
                className="overflow-hidden rounded-2xl cursor-pointer group relative"
                onClick={() => setLightbox(i + 9)}>
                <img src={src} alt={`Gallery ${i + 10}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
                {i === 3 && imgs.length > 13 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-black text-xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      +{imgs.length - 13} MORE
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 99999, background: 'rgba(0,0,0,0.95)' }}
            onClick={() => setLightbox(null)}>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full px-4"
              onClick={e => e.stopPropagation()}>

              <img
                src={imgs[lightbox]}
                alt=""
                className="w-full max-h-[80vh] object-contain rounded-2xl"
              />

              {/* Counter */}
              <p className="text-center text-white/50 text-sm mt-3">
                {lightbox + 1} / {imgs.length}
              </p>

              {/* Controls */}
              <button onClick={() => setLightbox(null)}
                className="absolute top-2 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                <XMarkIcon className="h-5 w-5" />
              </button>
              <button onClick={prev}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
              <button onClick={next}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ─── READY TO START (identical to Activities page) ────────────────────────────
export const ReadyToStart = () => (
  <section className="py-14 relative overflow-hidden"
    style={{ background: 'linear-gradient(135deg,#800020 0%,#3d0010 100%)' }}>
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
    </div>
    <div className="container-custom text-center relative z-10">
      <motion.div {...fade(0)}>
        <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
          Limited Spots Available
        </span>
        <h2 className="font-black text-white mb-4 leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem,5vw,4rem)' }}>
          READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
        </h2>
        <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
          Join PowerCore today and unlock access to world-class facilities, expert coaching and a community built around your goals. No excuses. Just results.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
            View Pricing &amp; Join <ArrowRightIcon className="h-4 w-4" />
          </Link>
          <Link to="/activities"
            className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
            View All Activities <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);